import { useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Search, ClipboardList, Plus, X } from 'lucide-react';
import { parseISO, differenceInDays, format } from 'date-fns';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import type { TooltipProps } from 'recharts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { AssessmentStatusChip } from '@/components/vendor-assessments/AssessmentStatusChip';
import { InfoTooltip } from '@/components/shared/InfoTooltip';
import { useAssessments, useAssessmentsOverview } from '@/api/hooks/useAssessments';
import { useAuthStore } from '@/store/auth.store';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import type { Assessment, AssessmentStatus, AssessmentFilters, AssessmentOverview } from '@/api/types/assessment.types';

// ── Lifecycle strip — all 13 states grouped by holder (Design card 5). sent_back is a legacy
//    equivalent of returned → folded under "With vendor", shown only when it has live rows. ──
const HOLDERS: { key: string; label: string; states: { s: string; label: string }[] }[] = [
  { key: 'not_started', label: 'Not started', states: [{ s: 'draft', label: 'Draft' }, { s: 'issued', label: 'Issued' }] },
  { key: 'with_vendor', label: 'With vendor', states: [{ s: 'in_progress', label: 'In progress' }, { s: 'returned', label: 'Returned' }, { s: 'reopened', label: 'Reopened' }, { s: 'sent_back', label: 'Sent back' }] },
  { key: 'waiting_on_us', label: 'Waiting on us', states: [{ s: 'submitted', label: 'Submitted' }, { s: 'resubmitted', label: 'Resubmitted' }, { s: 'under_review', label: 'Under review' }] },
  { key: 'closed', label: 'Closed', states: [{ s: 'approved', label: 'Approved' }, { s: 'rejected', label: 'Rejected' }, { s: 'expired', label: 'Expired' }, { s: 'cancelled', label: 'Cancelled' }] },
];

const STATUS_LABEL: Record<string, string> = Object.fromEntries(HOLDERS.flatMap((h) => h.states.map((st) => [st.s, st.label])));
const TERMINAL = new Set(['approved', 'rejected', 'expired', 'cancelled']);

// ── KPI config — copy VERIFIED against the getOverview predicate (honesty floor on help text). ──
const KPIS: { key: keyof AssessmentOverview['kpis']; label: string; info: string; filter?: [string, string] }[] = [
  { key: 'in_flight', label: 'In flight', info: 'Assessments actively moving through the pipeline — issued through review — excluding drafts and closed assessments.' },
  { key: 'overdue', label: 'Overdue', info: 'In-flight assessments past their due date. An assessment sitting with us for review still counts; a closed (terminal) assessment never does.', filter: ['overdue', 'true'] },
  { key: 'waiting_on_us', label: 'Waiting on us', info: 'Submitted, resubmitted or under review — the vendor has done their part and the next move is ours. This is the queue to work down.' },
  { key: 'awaiting_scoring', label: 'Awaiting scoring', info: 'Submitted assessments whose score has not been computed yet. Scoring runs asynchronously, so there is a real window where an assessment is complete but has no score — these show “Not scored yet”, never zero and never a default fail.' },
  { key: 'median_cycle_days', label: 'Median cycle time', info: 'Median days from issue to decision across completed (approved) assessments, from recorded timestamps. Shows “—” until at least one assessment has completed.' },
  { key: 'decisions_recorded', label: 'Decisions recorded', info: 'Assessments with a recorded decision — approved or rejected.' },
];

// ── Filter chips ──────────────────────────────────────────────────────────────
const FILTER_KEYS = ['status', 'overdue', 'outcome'] as const;
const OUTCOME_LABEL: Record<string, string> = { pass: 'Pass', conditional: 'Conditional', fail: 'Fail' };
function filterChipLabel(key: string, value: string): string {
  if (key === 'status') return STATUS_LABEL[value] ?? value;
  if (key === 'overdue') return 'Overdue';
  if (key === 'outcome') return `Outcome · ${OUTCOME_LABEL[value] ?? value}`;
  return `${key}: ${value}`;
}

// "Assessment outcomes" distribution tile (point-in-time counts). ⚠ Distinct from the P2 "Outcomes over
// time" chart (a monthly series) — this is the current total. Bar-click drills the assessment list by outcome.
const OUTCOME_DIST: [string, string, string][] = [
  ['pass', 'Pass', 'bg-cert-valid-stripe'], ['conditional', 'Conditional', 'bg-cert-warn-stripe'], ['fail', 'Fail', 'bg-cert-expired-stripe'],
];
function OutcomeDistTile({ by, onPick }: { by: AssessmentOverview['by_outcome']; onPick: (k: string) => void }) {
  const total = by.pass + by.conditional + by.fail;
  return (
    <div className="bg-bg-surface border border-border-subtle rounded-lg p-4 flex flex-col gap-3">
      <span className="text-sm font-semibold text-text-primary inline-flex items-center gap-1.5">Assessment outcomes
        <InfoTooltip text="All scored assessments by pass / conditional / fail, per the scoring thresholds (not-yet-scored excluded). A current total — distinct from “Outcomes over time”, which is the monthly trend. Click a bar to filter the table." />
      </span>
      {total === 0 ? (
        <p className="text-xs text-text-secondary">No scored assessments yet.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {OUTCOME_DIST.map(([key, label, cls]) => {
            const count = (by as Record<string, number>)[key] ?? 0;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            return (
              <button key={key} type="button" onClick={() => onPick(key)} className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity">
                <span className="w-20 shrink-0 text-xs text-text-secondary text-left">{label}</span>
                <div className="flex-1 h-2 rounded-full bg-bg-surface-2 overflow-hidden">
                  <div className={cn('h-full rounded-full', cls)} style={{ width: `${pct}%` }} />
                </div>
                <span className="w-8 shrink-0 text-right text-xs font-mono text-text-primary tabular-nums">{count}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Due / decision cell — exact-day overdue (in-flight ONLY); terminal rows show the decision date
//    (fixes the live "Overdue about 2 months ago" defect on approved cards). ──
function DueCell({ a }: { a: Assessment }) {
  if (TERMINAL.has(a.status)) {
    const decided = a.approved_at ?? a.reviewed_at ?? null;
    return decided
      ? <span className="text-xs text-text-secondary">{format(parseISO(decided), 'MMM d, yyyy')}</span>
      : <span className="text-xs text-text-secondary">—</span>;
  }
  if (!a.due_date) return <span className="text-xs text-text-secondary">—</span>;
  const due = parseISO(a.due_date);
  const overdueDays = differenceInDays(new Date(), due);
  if (overdueDays > 0) return <span className="text-xs font-medium text-accent-rose">Overdue {overdueDays}d</span>;
  const leftDays = differenceInDays(due, new Date());
  return <span className={cn('text-xs', leftDays < 7 ? 'text-accent-amber' : 'text-text-secondary')}>Due in {leftDays}d</span>;
}

// ── Outcome cell — pass/conditional/fail when scored; "Not scored yet" while null (never 0 / fail). ──
const OUTCOME_META: Record<string, { label: string; cls: string }> = {
  pass: { label: 'Pass', cls: 'bg-cert-valid-bg text-cert-valid-fg border-cert-valid-border' },
  conditional: { label: 'Conditional', cls: 'bg-cert-warn-bg text-cert-warn-fg border-cert-warn-border' },
  fail: { label: 'Fail', cls: 'bg-cert-expired-bg text-cert-expired-fg border-cert-expired-border' },
};
function OutcomeCell({ a }: { a: Assessment }) {
  const m = a.outcome ? OUTCOME_META[a.outcome] : null;
  if (!m) return <span className="text-xs text-text-faint italic">Not scored yet</span>;
  return <span className={cn('inline-flex items-center rounded-md border px-1.5 py-0.5 text-[11px] font-semibold', m.cls)}>{m.label}</span>;
}

// ── Completion cell (P3-B) — computed answered/total; "—" + no bar where null (never fabricated). ──
function CompletionCell({ a }: { a: Assessment }) {
  if (a.completion_pct == null) return <span className="text-xs text-text-secondary">—</span>;
  return (
    <div className="flex items-center gap-2 min-w-[84px]">
      <div className="flex-1 h-1.5 rounded-full bg-bg-surface-2 overflow-hidden">
        <div className="h-full rounded-full bg-accent-cyan" style={{ width: `${a.completion_pct}%` }} />
      </div>
      <span className="text-[11px] tabular-nums text-text-secondary w-8 text-right">{a.completion_pct}%</span>
    </div>
  );
}

// ── Attention (P3-A) — a VIEW of the same table records. Rows NAVIGATE (never mutate filter state). ──
const SEV_RAIL: Record<number, string> = { 1: 'bg-accent-rose', 2: 'bg-accent-amber', 3: 'bg-accent-cyan', 4: 'bg-text-muted' };
const SEV_BADGE: Record<number, string> = {
  1: 'bg-cert-expired-bg text-cert-expired-fg border-cert-expired-border',
  2: 'bg-cert-warn-bg text-cert-warn-fg border-cert-warn-border',
  3: 'bg-accent-cyan/10 text-accent-cyan border-accent-cyan/30',
  4: 'bg-bg-surface-2 text-text-muted border-border-subtle',
};
function AttentionRow({ row }: { row: AssessmentOverview['attention'][number] }) {
  return (
    <Link to={`/assessments/${row.assessment_id}`} className="flex items-center justify-between gap-3 py-2.5 -mx-4 px-4 hover:bg-bg-surface-2/60 transition-colors">
      <div className="flex items-center gap-2.5 min-w-0">
        <span className={cn('w-0.5 h-8 rounded-full shrink-0', SEV_RAIL[row.severity] ?? 'bg-text-muted')} />
        <div className="min-w-0">
          <p className="text-sm font-medium text-text-primary truncate">{row.vendor_name ?? '—'}</p>
          <p className="text-[11.5px] text-text-secondary truncate">{row.reason}</p>
        </div>
      </div>
      <span className={cn('text-[10px] font-bold rounded-md border px-1.5 py-0.5 shrink-0', SEV_BADGE[row.severity] ?? SEV_BADGE[4])}>{row.label}</span>
    </Link>
  );
}

// ── Page-size selector (Item 4 — reuses the /vendors 10/25/50/100 pattern, adjusted to 25/50/100/All).
//    ⚠ "All" maps to 200 — the server caps per_page at 200; the pre-prod lists are small, but the cap is
//    the honest interim (a true unbounded "All" is deferred with virtualization). ──
const PAGE_SIZES: (number | 'All')[] = [25, 50, 100, 'All'];
const ALL_CAP = 200;
function PageSizeSelect({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] text-text-secondary">Show</span>
      {PAGE_SIZES.map((n) => {
        const num = n === 'All' ? ALL_CAP : n;
        return (
          <button key={String(n)} type="button" onClick={() => onChange(num)}
            title={n === 'All' ? `Up to ${ALL_CAP} (server cap)` : undefined}
            className={cn('text-[11px] font-semibold rounded px-1.5 py-0.5 transition-colors',
              value === num ? 'bg-accent-cyan/15 text-accent-cyan' : 'text-text-secondary hover:text-text-primary')}>
            {n}
          </button>
        );
      })}
    </div>
  );
}

// ── KPI card ──────────────────────────────────────────────────────────────────
function Kpi({ label, value, info, onClick }: { label: string; value: string; info: string; onClick?: () => void }) {
  return (
    <div onClick={onClick}
      className={cn('bg-bg-surface border border-border-subtle rounded-lg p-3.5 flex flex-col gap-1', onClick && 'cursor-pointer hover:border-accent-cyan/50 transition-colors')}>
      <span className="text-[10px] font-bold uppercase tracking-wider text-text-secondary inline-flex items-center gap-1">{label}<InfoTooltip text={info} /></span>
      <span className="text-2xl font-bold text-text-primary tabular-nums">{value}</span>
    </div>
  );
}

// ── P2 charts ─────────────────────────────────────────────────────────────────
// Token hexes (Trap #55 — recharts needs color strings; mirror tailwind.config.ts).
const C = { valid: '#3FB980', warn: '#D7A94A', expired: '#C2453F', cyan: '#009FFD', axis: '#5A6A82', grid: '#1E2E42' };

function ChartPanel({ title, info, subtitle, children }: { title: string; info: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="bg-bg-surface border border-border-subtle rounded-lg p-4 flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-text-primary inline-flex items-center gap-1.5">{title}<InfoTooltip text={info} /></span>
        {subtitle && <span className="text-[11px] text-text-secondary">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

function NotEnough({ note }: { note: string }) {
  return (
    <div className="h-[170px] flex flex-col items-center justify-center gap-1.5 text-center px-4 border border-dashed border-border-subtle rounded-lg">
      <span className="text-[11px] uppercase tracking-wide text-text-faint">Not enough data</span>
      <span className="text-[11.5px] text-text-faint leading-relaxed max-w-[36ch]">{note}</span>
    </div>
  );
}

// Chart 1 — "Where the time goes": three clean stage cards, MEDIAN as the primary value, with a
// plain-language 90%-within line + completed count below. A stage with no completed transitions shows
// the honest "No completed transitions yet" (never a fabricated 0). (Items 1 + 2.)
function CycleStages({ stages }: { stages: AssessmentOverview['cycle_stages'] }) {
  if (!stages.some((s) => s.count > 0)) {
    return <NotEnough note="No completed transitions yet — stage medians appear as assessments move through submission and review." />;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {stages.map((s) => (
        <div key={s.stage} className="rounded-lg border border-border-subtle bg-bg-base/40 p-3 flex flex-col gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-text-secondary">{s.label}</span>
          {s.count > 0 && s.median_days != null ? (
            <>
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-bold text-text-primary tabular-nums">{s.median_days}</span>
                <span className="text-xs text-text-secondary">days median</span>
              </div>
              <div className="flex flex-col gap-0.5 text-[11px] text-text-secondary pt-1 border-t border-border-subtle/60">
                {s.p90_days != null && <span>90% completed within {s.p90_days} days</span>}
                <span>{s.count} completed</span>
              </div>
            </>
          ) : (
            <span className="text-xs text-text-faint italic mt-1">No completed transitions yet</span>
          )}
        </div>
      ))}
    </div>
  );
}

// ⚠ Tooltip OMITS zero segments — a month with no fails must never read "0 fail".
function OutcomeTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const nonZero = payload.filter((p) => Number(p.value ?? 0) > 0);
  if (!nonZero.length) return null;
  return (
    <div style={{ background: '#0E1A2B', border: `1px solid ${C.grid}`, borderRadius: 8, fontSize: 12, padding: '6px 10px' }}>
      <div style={{ color: '#EAF2FB', marginBottom: 2 }}>{label}</div>
      {nonZero.map((p) => <div key={p.name} style={{ color: p.color }}>{p.name}: {p.value}</div>)}
    </div>
  );
}

// Chart 2 — "Outcomes over time": monthly stacked pass/conditional/fail. A 0/0/0 month draws no bar
// (a GAP, never "0 fail"). NULL/not-yet-scored are excluded server-side (the async window).
function OutcomesTrend({ trend }: { trend: AssessmentOverview['outcome_trend'] }) {
  const rows = trend.buckets.map((b, i) => ({
    x: format(parseISO(b), 'MMM'),
    pass: trend.pass[i], conditional: trend.conditional[i], fail: trend.fail[i],
    total: trend.pass[i] + trend.conditional[i] + trend.fail[i],
  }));
  if (!rows.some((r) => r.total > 0)) {
    return <NotEnough note="No scored assessments in the last 12 months yet — outcomes appear once assessments are scored." />;
  }
  return (
    <ResponsiveContainer width="100%" height={170}>
      <BarChart data={rows} margin={{ top: 6, right: 8, bottom: 0, left: -18 }}>
        <CartesianGrid stroke={C.grid} vertical={false} />
        <XAxis dataKey="x" tick={{ fill: C.axis, fontSize: 10 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
        <YAxis tick={{ fill: C.axis, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} width={28} />
        <Tooltip content={<OutcomeTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
        <Bar dataKey="pass" stackId="o" name="pass" fill={C.valid} maxBarSize={22} />
        <Bar dataKey="conditional" stackId="o" name="conditional" fill={C.warn} maxBarSize={22} />
        <Bar dataKey="fail" stackId="o" name="fail" fill={C.expired} radius={[2, 2, 0, 0]} maxBarSize={22} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export function AssessmentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasAnyRole } = useAuthStore();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);       // Item 4 — 25 default; 25/50/100/All
  const [attnShowAll, setAttnShowAll] = useState(false); // Item 3 — collapse the attention panel to ~10
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [IssueModal, setIssueModal] = useState<React.ComponentType<{ open: boolean; onClose: () => void }> | null>(null);

  const searchTerm = searchParams.get('search') ?? '';
  // ⚠ Item 5 — LOCAL search text, NOT tied to the URL. The prior bug: the input's value was derived from
  // the same `search` URL param its onChange updated via setSearchParams, so keystrokes were swallowed
  // (the controlled value lagged the async URL write) until an unrelated state change reconciled the
  // params. Typed text now lives in local state and is committed to the URL only on Enter / Search-click,
  // composing with the chip params.
  const [searchInput, setSearchInput] = useState(searchTerm);
  const canIssue = hasAnyRole(['risk_manager', 'admin', 'super_admin']);

  // URL = the source of truth for filters (composable, shareable — same model as /vendors).
  const setFilter = useCallback((key: string, value: string | null) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value == null || value === '') p.delete(key); else p.set(key, value);
      return p;
    });
    setPage(1);
  }, [setSearchParams]);

  const submitSearch = useCallback(() => setFilter('search', searchInput.trim() || null), [setFilter, searchInput]);
  const clearAll = useCallback(() => { setSearchParams(new URLSearchParams()); setSearchInput(''); setPage(1); }, [setSearchParams]);

  const filters: AssessmentFilters = {
    search: searchTerm || undefined,
    status: (searchParams.get('status') as AssessmentStatus) || undefined,
    overdue_only: searchParams.get('overdue') === 'true' || undefined,
    outcome: (searchParams.get('outcome') as 'pass' | 'conditional' | 'fail') || undefined,
  };

  const { data, isLoading } = useAssessments(filters, page, pageSize);
  const { data: overview } = useAssessmentsOverview();
  const assessments = data?.data ?? [];
  const meta = data?.meta;

  // Active filter chips (status + overdue) — same-facet REPLACES (one status key), cross-facet COMBINES.
  const activeChips = FILTER_KEYS
    .map((k) => [k as string, searchParams.get(k)] as [string, string | null])
    .filter((e): e is [string, string] => e[1] != null);

  const openIssueModal = useCallback(async () => {
    if (!IssueModal) {
      const mod = await import('@/features/assessments/GlobalIssueAssessmentModal');
      setIssueModal(() => mod.GlobalIssueAssessmentModal);
    }
    setIssueModalOpen(true);
  }, [IssueModal]);

  const kpiValue = (k: keyof AssessmentOverview['kpis']): string => {
    if (!overview) return '—';
    const v = overview.kpis[k];
    if (k === 'median_cycle_days') return v == null ? '—' : `${v}d`;
    return String(v);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Assessments</h1>
          <p className="mt-1 text-sm text-text-secondary">Where every assessment is, and which ones are waiting on us</p>
        </div>
        {canIssue && (
          <Button size="sm" onClick={openIssueModal}><Plus className="h-4 w-4 mr-1.5" />Issue Assessment</Button>
        )}
      </div>

      {/* Search (half-width) + Search button + composable filter chips in the field. ⚠ Item 5 — the text
          commits on Enter / Search-click (local state), not per keystroke, and composes with the chips. */}
      <div className="flex items-center gap-2 w-full md:max-w-[52%]">
        <div className={cn('flex-1 min-w-0 flex items-center gap-1.5 flex-wrap rounded-md border bg-bg-surface px-2.5 py-1.5 transition-colors',
          activeChips.length > 0 ? 'border-accent-cyan/60 ring-1 ring-accent-cyan/25' : 'border-border-subtle')}>
          <Search className="h-4 w-4 shrink-0 text-text-secondary" />
          {activeChips.map(([key, value]) => (
            <button key={key} type="button" onClick={() => setFilter(key, null)}
              className="inline-flex items-center gap-1 rounded border border-accent-cyan/40 bg-accent-cyan/10 px-1.5 py-0.5 text-[11px] font-medium text-accent-cyan hover:bg-accent-cyan/20 transition-colors">
              {filterChipLabel(key, value)}<X className="h-3 w-3" />
            </button>
          ))}
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitSearch(); }}
            placeholder={activeChips.length > 0 ? 'Add a vendor or template search…' : 'Search vendor or template…'}
            className="flex-1 min-w-[120px] bg-transparent text-sm text-text-primary placeholder:text-text-secondary focus:outline-none py-0.5"
          />
          {activeChips.length > 0 && (
            <button type="button" onClick={clearAll} className="shrink-0 text-[11px] text-text-secondary hover:text-text-primary underline">Clear all</button>
          )}
        </div>
        <Button size="sm" onClick={submitSearch}>Search</Button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3">
        {KPIS.map((k) => (
          <Kpi key={k.key} label={k.label} value={kpiValue(k.key)} info={k.info}
            onClick={k.filter ? () => setFilter(k.filter![0], k.filter![1]) : undefined} />
        ))}
      </div>

      {/* Lifecycle strip — 12 states by holder, greyed zeros, no view toggle (Design cards 1/5/13) */}
      <div className="bg-bg-surface border border-border-subtle rounded-lg p-4">
        <div className="flex items-center gap-1.5 mb-3">
          <span className="text-sm font-semibold text-text-primary">Lifecycle — all 13 states</span>
          <InfoTooltip text="Every lifecycle state grouped by who holds the next action. Counts are real status counts; a state with zero records stays visible, greyed at 0. Click a state to filter the table." />
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
          {HOLDERS.map((h) => {
            // Item 1 — render ALL 13 states (sent_back legacy included), greyed at 0 like any empty state.
            const states = h.states;
            return (
              <div key={h.key} className="flex flex-col gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-text-secondary">{h.label}</span>
                <div className="flex flex-wrap gap-1.5">
                  {states.map((st) => {
                    const count = overview?.lifecycle_counts[st.s] ?? 0;
                    const active = searchParams.get('status') === st.s;
                    return (
                      <button key={st.s} type="button" onClick={() => setFilter('status', active ? null : st.s)}
                        className={cn('inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors',
                          active ? 'border-accent-cyan bg-accent-cyan/10 text-accent-cyan'
                            : count > 0 ? 'border-border-subtle text-text-primary hover:border-accent-cyan/50'
                              : 'border-border-subtle/50 text-text-faint')}>
                        <span>{st.label}</span>
                        <span className="font-mono tabular-nums font-semibold">{overview ? count : '·'}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* P2 charts — cycle-time medians + outcomes over time (Design page order: strip → charts → table) */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(340px,1fr))] gap-3">
        <ChartPanel title="Where the time goes"
          info="Median days per lifecycle transition, from recorded state-change timestamps, over completed transitions only. “90% completed within N days” is the P90 — 90% of completed assessments moved through this stage within that time; the slowest 10% took longer. A stage with no completed transitions shows no value (never a fabricated 0). No SLA target."
          subtitle="median · 90%-within · completed">
          {overview ? <CycleStages stages={overview.cycle_stages} /> : <NotEnough note="Loading…" />}
        </ChartPanel>
        <ChartPanel title="Outcomes over time"
          info="Scored assessments by month (last 12), split pass / conditional / fail via the scoring thresholds. Not-yet-scored assessments are excluded (they are the async window, not an outcome); a month with no scored assessments is a gap, never “0 fail”."
          subtitle="pass / conditional / fail · monthly">
          {overview ? <OutcomesTrend trend={overview.outcome_trend} /> : <NotEnough note="Loading…" />}
        </ChartPanel>
        {/* Item 3 — "Assessment outcomes" distribution (moved from /vendors). Current total, distinct from
            the "Outcomes over time" series above; bar-click drills the table by outcome (reflected in a chip). */}
        {overview && <OutcomeDistTile by={overview.by_outcome} onPick={(k) => setFilter('outcome', searchParams.get('outcome') === k ? null : k)} />}
      </div>

      {/* Attention — a VIEW of the same table records (Design page order: charts → attention → table).
          4 real conditions, severity-first; rows NAVIGATE (distinct from the filter gesture). */}
      <div className="bg-bg-surface border border-severity-high/40 rounded-lg p-4 flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-semibold text-text-primary inline-flex items-center gap-1.5">Requires attention
            <InfoTooltip text="Assessments that need action, from four real conditions: past due · in our queue beyond 7 days · scored but no decision recorded · returned with no resubmission. Each row is a real assessment in the table below; click to open it." />
          </span>
          {overview && <Badge variant={overview.attention.length > 0 ? 'rose' : 'muted'} className="font-mono">{overview.attention.length}</Badge>}
        </div>
        {!overview ? (
          <p className="text-xs text-text-secondary">—</p>
        ) : overview.attention.length === 0 ? (
          <p className="text-sm text-text-secondary">Nothing waiting on us.</p>
        ) : (
          <>
            {/* ⚠ Item 3 — ~10 rows in a fixed-height scroll so a long queue never dominates the page;
                "View all" expands to the full list (records unchanged — presentation only). */}
            <div className={cn('flex flex-col divide-y divide-border-subtle overflow-y-auto', !attnShowAll && 'max-h-[360px]')}>
              {(attnShowAll ? overview.attention : overview.attention.slice(0, 10)).map((row) => (
                <AttentionRow key={`${row.assessment_id}-${row.signal}`} row={row} />
              ))}
            </div>
            {overview.attention.length > 10 && (
              <button type="button" onClick={() => setAttnShowAll((v) => !v)}
                className="self-start text-xs font-medium text-accent-cyan hover:text-accent-cyan/80 transition-colors">
                {attnShowAll ? 'Show fewer' : `View all ${overview.attention.length}`}
              </button>
            )}
          </>
        )}
      </div>

      {/* Table — Vendor · Template · Status · Completion · Issued · Due · Outcome · Actions */}
      <div className="bg-bg-surface border border-border-subtle rounded-lg overflow-hidden">
        {/* Item 4 — total + page-size selector (25/50/100/All) */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border-subtle">
          <span className="text-xs text-text-secondary">{meta ? `${meta.total} assessment${meta.total !== 1 ? 's' : ''}` : ''}</span>
          <PageSizeSelect value={pageSize} onChange={(n) => { setPageSize(n); setPage(1); }} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="sticky top-0 bg-bg-surface border-b border-border-subtle">
              <tr>
                {['Vendor', 'Template', 'Status', 'Completion', 'Issued', 'Due', 'Outcome', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {isLoading
                ? [...Array(6)].map((_, i) => (
                    <tr key={i}>{[...Array(8)].map((_, j) => <td key={j} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td>)}</tr>
                  ))
                : assessments.map((a) => (
                    <tr key={a.id} onClick={() => navigate(`/assessments/${a.id}`)}
                      className="cursor-pointer hover:bg-bg-surface-2 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-text-primary">{a.vendor_name ?? '—'}</span>
                          {a.vendor_tier && <Badge variant="muted" className="text-[10px] py-0">{a.vendor_tier.replace('tier_', 'T').replace(/_.*/, '')}</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-text-secondary">{a.template_name ?? '—'}</td>
                      <td className="px-4 py-3"><AssessmentStatusChip status={a.status} /></td>
                      <td className="px-4 py-3"><CompletionCell a={a} /></td>
                      <td className="px-4 py-3 text-xs text-text-secondary">{a.issued_at ? format(parseISO(a.issued_at), 'MMM d, yyyy') : '—'}</td>
                      <td className="px-4 py-3"><DueCell a={a} /></td>
                      <td className="px-4 py-3"><OutcomeCell a={a} /></td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate(`/assessments/${a.id}`)}>View</Button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
        {!isLoading && assessments.length === 0 && (
          <div className="py-12">
            <EmptyState
              icon={ClipboardList}
              title={activeChips.length > 0 || searchTerm ? 'No assessments match these filters' : 'No assessments yet'}
              description={activeChips.length > 0 || searchTerm ? 'Remove a chip above or Clear all to widen the search.' : (canIssue ? 'Issue your first assessment to start evaluating vendor security.' : undefined)}
              action={canIssue && activeChips.length === 0 && !searchTerm ? { label: 'Issue your first assessment', onClick: openIssueModal } : undefined}
            />
          </div>
        )}
        {meta && meta.total_pages > 1 && (
          <div className="flex items-center justify-between border-t border-border-subtle px-4 py-3">
            <p className="text-xs text-text-secondary">Page {meta.page} of {meta.total_pages} · {meta.total} total</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <Button variant="outline" size="sm" disabled={page >= meta.total_pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>

      {IssueModal && <IssueModal open={issueModalOpen} onClose={() => setIssueModalOpen(false)} />}
    </div>
  );
}
